import type { RequestPayload } from "@inertiajs/core";
import { router } from "@inertiajs/react";
import { Anchor, Autocomplete, Button, Menu, MenuDropdown, MultiSelect, Select } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { IconDotsVertical, IconFileExport, IconFileImport } from "@tabler/icons-react";
import type { DataTableColumn, DataTableSortStatus } from "mantine-datatable";
import { useState } from "react";
import { ImportModal } from "../../../components/modals/ImportModal";
import { FilterSelect } from "../../../components/select/FilterSelect";
import { WeekdayMultiSelect } from "../../../components/select/WeekdayMultiSelect";
import { getFullName } from "../../../helpers/stringHelper";
import { useRole } from "../../../hooks/useProps";
import { useYearContext } from "../../../hooks/useYearContext";
import type { Apprentice } from "../../../models/apprentice";
import { getOccupationOptions } from "../../../models/enums/occupation";
import { Role } from "../../../models/enums/role";
import { type Group, getGroupOptions } from "../../../models/group";
import type { Pagination } from "../../../models/pagination";
import { getSchoolOptions, type School } from "../../../models/school";
import {
  apprentice_path,
  apprentice_supervisor_path,
  apprentices_path,
  edit_apprentice_path,
  export_apprentices_path,
  import_apprentices_path,
  import_example_apprentices_path,
  new_apprentice_path,
  serialize,
} from "../../../routes";
import Users, { resetUserFilter, type UserFilterFormType } from "../Users";

type ApprenticesProps = {
  apprentices: Apprentice[];
  apprentice_names: string[];
  apprentice_shorts: string[];
  supervisor_names: string[];
  company_names: string[];
  groups: Group[];
  schools: School[];
  class_names: string[];
  filter: FilterFormType & {
    sort_key?: "name" | "supervisor.name" | "email" | "occupation" | "company.name" | "group.name";
    sort_direction?: "asc" | "desc";
  };
  pagination: Pagination;
};

type FilterFormType = UserFilterFormType & {
  short?: string;
  supervisor_name?: string;
  company_name?: string;
  group_id?: string;
  occupations?: string[];
  bms?: string;
  days?: string[];
  school_id?: string;
  class_name?: string;
};

const resetFilters: FilterFormType = {
  short: undefined,
  supervisor_name: undefined,
  company_name: undefined,
  group_id: undefined,
  occupations: undefined,
  bms: undefined,
  class_name: undefined,
  days: [],
  ...resetUserFilter,
};

export default function Apprentices({
  apprentices,
  company_names,
  groups,
  filter,
  apprentice_names,
  apprentice_shorts,
  supervisor_names,
  schools,
  class_names,
  pagination,
}: ApprenticesProps) {
  const [opened, { open, close }] = useDisclosure(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const role = useRole();
  const showExtendedFilters = role === Role.ADMIN || role === Role.COACH;
  const { sort_key, sort_direction, occupations, ...filterFormData } = filter;
  const initialFormData = {
    occupations: occupations ?? [],
    ...filterFormData,
  };
  const { pathWithYearContext } = useYearContext();

  const sortStatus: DataTableSortStatus<Apprentice> = {
    columnAccessor: sort_key || "",
    direction: sort_direction || "asc",
  };

  const onSortChange = (sortStatus: DataTableSortStatus<Apprentice>) => handlePageUpdate(initialFormData, sortStatus, pagination.page);
  const onFilterSubmit = (values: FilterFormType) => handlePageUpdate(values, sortStatus, pagination.page);
  const onPageChange = (page: number) => handlePageUpdate(initialFormData, sortStatus, page);

  const handlePageUpdate = (filters: FilterFormType, sortStatus: DataTableSortStatus<Apprentice>, page: number) => {
    setIsFetching(true);
    router.visit(pathWithYearContext(apprentices_path), {
      only: ["apprentices", "filter", "pagination"],
      data: {
        filter: {
          ...filters,
          sort_key: sortStatus.columnAccessor,
          sort_direction: sortStatus.direction,
        },
        page,
      },
      onFinish: () => setIsFetching(false),
    });
  };

  const downloadPath = () =>
    `${pathWithYearContext(export_apprentices_path)}?${serialize({
      filter: {
        ...initialFormData,
        sort_key: sortStatus.columnAccessor,
        sort_direction: sortStatus.direction,
      },
    })}`;

  const additionalColumns: DataTableColumn<Apprentice>[] = [
    {
      accessor: "short",
      title: "Kürzel",
      sortable: true,
    },
    {
      accessor: "company.name",
      sortable: true,
      title: "Lehrbetrieb",
    },
    {
      accessor: "supervisor.name",
      title: "Berufsbildner/in",
      sortable: true,
      render: (apprentice) =>
        apprentice.apprentice_supervisor && (
          <Anchor onClick={() => router.get(pathWithYearContext(apprentice_supervisor_path, apprentice.apprentice_supervisor.id))}>
            {getFullName(apprentice.apprentice_supervisor)}
          </Anchor>
        ),
    },
    {
      accessor: "group.name",
      sortable: true,
      title: "Gruppe",
    },
    {
      accessor: "schools",
      title: "Schule",
      sortable: true,
      render: (apprentice) =>
        apprentice.school_days
          .filter((school_day) => !school_day.school.bms)
          .map((school_day) => `${school_day.school.short}`)
          .join(", "),
    },
    {
      accessor: "bms",
      sortable: true,
      title: "BMS",
      render: (apprentice) =>
        apprentice.school_days
          .filter((school_day) => school_day.school.bms)
          .map((school_day) => `${school_day.school.short}`)
          .join(", "),
    },
  ];

  const onView = (apprentice: Apprentice) => router.get(pathWithYearContext(apprentice_path, apprentice.id));
  const onDelete = (apprentice: Apprentice) => router.delete(pathWithYearContext(apprentice_path, apprentice.id));
  const onEdit = (apprentice: Apprentice) => router.get(pathWithYearContext(edit_apprentice_path, apprentice.id));
  const onNew = () => router.get(pathWithYearContext(new_apprentice_path));
  const onImport = (values: RequestPayload) => {
    router.post(pathWithYearContext(import_apprentices_path), values, {
      onFinish: () => {
        setIsLoading(false);
        close();
      },
    });
  };

  const getAdditionalFilters = (filterForm: UseFormReturnType<FilterFormType>) => [
    <Autocomplete data={apprentice_shorts} label="Kürzel" key={filterForm.key("short")} {...filterForm.getInputProps("short")} />,
    ...(showExtendedFilters
      ? [
          <Autocomplete
            label="Lehrbetrieb"
            clearable
            data={company_names}
            key={filterForm.key("company_name")}
            {...filterForm.getInputProps("company_name")}
          />,
        ]
      : []),
    ...(showExtendedFilters
      ? [
          <Autocomplete
            data={supervisor_names}
            label="Berufsbildner/in Name"
            key={filterForm.key("supervisor_name")}
            {...filterForm.getInputProps("supervisor_name")}
          />,
        ]
      : []),
    <MultiSelect
      label="Beruf"
      clearable
      data={getOccupationOptions()}
      key={filterForm.key("occupations")}
      {...filterForm.getInputProps("occupations")}
    />,
    <FilterSelect
      label="Gruppe"
      clearable
      data={getGroupOptions(groups, false)}
      key={filterForm.key("group_id")}
      {...filterForm.getInputProps("group_id")}
    />,
    <FilterSelect
      label="Schule"
      clearable
      data={getSchoolOptions(schools)}
      key={filterForm.key("school_id")}
      {...filterForm.getInputProps("school_id")}
    />,
    <Autocomplete
      label="Klasse"
      clearable
      data={class_names}
      key={filterForm.key("class_name")}
      {...filterForm.getInputProps("class_name")}
    />,
    <WeekdayMultiSelect label="Schultage" clearable key={filterForm.key("days")} {...filterForm.getInputProps("days")} />,
    <Select
      label="BMS"
      data={[
        { value: "true", label: "Ja" },
        { value: "false", label: "Nein" },
      ]}
      clearable
      key={filterForm.key("bms")}
      {...filterForm.getInputProps("bms")}
    />,
  ];

  const actionButtons = [
    <Menu shadow="md" key="apprentice-import/export">
      <Menu.Target>
        <Button leftSection={<IconDotsVertical />} variant="light">
          Import/Export
        </Button>
      </Menu.Target>
      <MenuDropdown>
        <Menu.Item leftSection={<IconFileImport />} onClick={() => open()}>
          Lernende importieren
        </Menu.Item>
        <Menu.Item
          component="a"
          leftSection={<IconFileExport />}
          href={downloadPath()}
          variant="light"
          disabled={pagination.count === 0}
          download
        >
          {pagination.count} Lernende exportieren
        </Menu.Item>
      </MenuDropdown>
    </Menu>,
  ];

  return (
    <>
      <ImportModal
        opened={opened}
        close={close}
        title="Lernende importieren"
        onSubmit={onImport}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        downloadPath={pathWithYearContext(import_example_apprentices_path)}
      />
      <Users<Apprentice, FilterFormType>
        actionButtons={actionButtons}
        onFilterSubmit={onFilterSubmit}
        filterFormData={initialFormData}
        getAdditionalFilters={getAdditionalFilters}
        viewOnly={role !== Role.ADMIN}
        onNew={onNew}
        onDelete={onDelete}
        onEdit={onEdit}
        onView={onView}
        title="Lernende"
        userTypeString="Lernende/r"
        userTypeStringPlural="Lernende"
        records={apprentices}
        userNames={apprentice_names}
        onReset={() => onFilterSubmit(resetFilters)}
        additionalColumns={additionalColumns}
        sortStatus={sortStatus}
        onSortStatusChange={onSortChange}
        pagination={pagination}
        onPageChange={onPageChange}
        fetching={isFetching}
      />
    </>
  );
}
